import type { DispatchTableRow } from "./dispatchTableRow";
import { rowFeedId } from "./dispatchTableRow";

/** One channel a fleet message goes to, with the driver's name for the summary. */
export type FleetRecipient = { feedId: string; name: string };

/** Up to this many recipients are named; past it the summary counts them. */
export const NAMED_RECIPIENT_LIMIT = 3;

/**
 * The channels a fleet message from the Dispatch page goes to: one per driver among `rows`, in row order, rows without
 * a channel skipped. Message rows collapse to their driver's channel, so a driver with three messages ticked is one
 * recipient.
 */
export function fleetRecipientsFor(rows: readonly DispatchTableRow[]): FleetRecipient[] {
  const seen = new Set<string>();
  const recipients: FleetRecipient[] = [];
  for (const row of rows) {
    const feedId = rowFeedId(row);
    if (!feedId || seen.has(feedId)) continue;
    seen.add(feedId);
    recipients.push({ feedId, name: row.driver.name ?? row.driver.alias ?? "Driver" });
  }
  return recipients;
}

/**
 * Who the message goes to, in a phrase: the names when there are a few ("Al Alvarez, Bo Barker and Cy Colton"), a count
 * when there are many ("312 drivers"), and "no one" when the list is empty.
 */
export function summarizeRecipients(recipients: readonly FleetRecipient[]): string {
  if (recipients.length === 0) return "no one";
  if (recipients.length > NAMED_RECIPIENT_LIMIT) return `${recipients.length} drivers`;
  const names = recipients.map((recipient) => recipient.name);
  if (names.length === 1) return names[0] as string;
  return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
}
